import { isValidIsraeliID } from '@/helpers/israeliIdValidator';
import { UNLIMITED_PAYMENTS } from '../standingOrderModel';
import type { AptOrderState } from '../bankStandingOrderActions';

/*
 * HAT-1478: a new bank standing order from the bank board, written straight into the office's APT -
 * the last of the bank actions Druk asked for in HAT-1441 («כל סוגי הפעולות… כמו בדף הו"ק אשראי»).
 *
 * The flow is the office's own (HAT-1478, 29.10.2026): first whether the donor's bank already holds a
 * mandate for the order. If it does, the order's details and nothing else. If it does not, the signed
 * mandate form is uploaded too, it goes to the donor's bank, and the bank needs at least ten days to
 * set the mandate up - so the first collection is the first billing day ten days away or more. The
 * details are the same in both: the project, the donor and their ID, the account, the amount, the
 * payments and the billing day, and an e-mail address if the coordinator has one. The rules are
 * repeated by the server; they are here so the form says what is wrong before anything is sent.
 *
 * How the form reaches the bank: Bank Hapoalim takes it by e-mail, and the site sends it there. Every
 * other bank takes it only by fax to the branch, and the coordinator chooses - fax it himself, or hand
 * it to the office, whose secretaries fax it as they do today (Druk, 1.11.2026: no fax service).
 */

/** Must match FeatureFlagKeys.BankStandingOrderCreate and SeedFeatureFlag_BankStandingOrderCreate.sql. */
export const BANK_STANDING_ORDER_CREATE_FEATURE_KEY = 'bank-standing-order-create';

/** The days a bank order collects on (the office's list, and what AptBankOrder.cs found in APT). */
export const BANK_BILLING_DAYS = [1, 5, 10, 15, 20, 25] as const;

/** APT reads 999 payments as "until the donor stops", so a limited order has at most one fewer. */
export const MAX_LIMITED_PAYMENTS = UNLIMITED_PAYMENTS - 1;

/**
 * The office: «נדרשים לפחות 10 ימים בין מועד שליחת הטופס לבנק לבין מועד שידור הוראת הקבע למס"ב».
 * The form goes to the bank when the order is set up, so the first collection is at least this far
 * from that day.
 */
export const MANDATE_SETUP_DAYS = 10;

/** The signed form: a photo or a scan. Ten megabytes holds a phone photo with room to spare. */
export const MANDATE_FORM_MAX_BYTES = 10 * 1024 * 1024;
export const MANDATE_FORM_ACCEPT = 'image/*,application/pdf';

export const createBankOrderUrl = (fundId: number): string => `bank-standing-orders/${fundId}/new`;

/** The bank that takes the form by e-mail (fax100@poalim.co.il, the office's spec); the site sends it there. */
export const BANK_BY_EMAIL = '12';

/** Where a branch's fax number is looked up - the page the office gave in its spec. */
export const BRANCH_FAX_LOOKUP_URL = 'https://reports.matara.pro/masav/fax?MosadId=7002169';

/**
 * How the signed form goes to the bank: by the site's e-mail (Bank Hapoalim), by the coordinator's own
 * fax, or through the office, which faxes it as it does today.
 */
export type FormDelivery = 'bankEmail' | 'self' | 'office';

/** Whether the donor's bank already holds a mandate for the order; '' until the coordinator says. */
export type MandateAnswer = '' | 'exists' | 'missing';

export interface BankOrderForm {
  mandate: MandateAnswer;
  donorName: string;
  israeliId: string;
  email: string;
  /** The bank's number, as the bank list gives it. */
  bank: string;
  /** "12 - בנק הפועלים", for the branch list to depend on. */
  bankDisplay: string;
  branch: string;
  /** "177 - בני ברק", so the field shows the branch again when the coordinator steps back to it. */
  branchDisplay: string;
  account: string;
  amount: string;
  payments: string;
  /** "Until the donor stops" - chosen on purpose, since the number of payments must be given. */
  unlimited: boolean;
  billingDay: string;
  /** For a bank other than Hapoalim: who faxes the form; '' until the coordinator says. */
  faxBy: '' | 'self' | 'office';
}

export const emptyBankOrderForm = (): BankOrderForm => ({
  mandate: '',
  donorName: '',
  israeliId: '',
  email: '',
  bank: '',
  bankDisplay: '',
  branch: '',
  branchDisplay: '',
  account: '',
  amount: '',
  payments: '',
  unlimited: false,
  billingDay: '',
  faxBy: '',
});

export type BankOrderField = keyof BankOrderForm | 'mandateForm';

export type BankOrderError =
  | 'required'
  | 'israeliIdInvalid'
  | 'emailInvalid'
  | 'accountInvalid'
  | 'amountRequired'
  | 'paymentsRequired'
  | 'paymentsInvalid'
  | 'faxByRequired'
  | 'mandateFormRequired'
  | 'mandateFormTooLarge'
  | 'mandateFormType';

export type BankOrderErrors = Partial<Record<BankOrderField, BankOrderError>>;

/** What the server is sent. Bank and branch go zero-padded, the way APT holds them («09», «004»). */
export interface CreateBankStandingOrderRequest {
  /** Made once per form, so a second press of the button - or a retry - cannot make a second order. */
  requestId: string;
  /** The bank already holds a mandate; when not, the signed form comes with the request. */
  mandateExists: boolean;
  donorName: string;
  israeliId: string;
  email?: string;
  bank: string;
  branch: string;
  account: string;
  monthlyAmount: number;
  /** null: until the donor stops. */
  payments: number | null;
  billingDay: number;
  /** yyyy-MM-dd - as the form showed it; the server works it out again and refuses a different one. */
  firstCollection: string;
  /** Only when there is no mandate yet. */
  formDelivery?: FormDelivery;
  mandateFormFileName?: string;
  mandateFormContentType?: string;
  /** The file, base64, without the data-URL prefix. */
  mandateFormBase64?: string;
}

export interface CreateBankStandingOrderResult {
  /** The order's APT movement number - its id on the board. */
  movementId: number;
  /** yyyy-MM-dd */
  firstCollection: string;
  order: AptOrderState;
  /** Whether the signed form went out; null when there was none (the mandate exists). */
  formSent: boolean | null;
}

// ---------------------------------------------------------------- dates

const startOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const pad = (n: number, width = 2): string => String(n).padStart(width, '0');

export const isoDate = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** yyyy-MM-dd as a local date, or null. */
export const readIsoDate = (text: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(text ?? '');
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

/** The billing day of a month, or its last day when the month is shorter - as APT collects. */
const billingDayIn = (year: number, month: number, day: number): Date =>
  new Date(year, month, Math.min(day, new Date(year, month + 1, 0).getDate()));

/**
 * The first collection: the first billing day from the earliest the bank can collect - tomorrow
 * when the mandate exists (today's file has gone to the bank), ten days on when the form still has
 * to reach the bank. A date the office has locked for its file is passed over for the next month:
 * the file for it is made, and the order would not be in it.
 */
export function firstCollectionDate(
  billingDay: number,
  mandate: MandateAnswer,
  lockedDates: readonly string[] | undefined,
  now: Date = new Date(),
): Date | null {
  if (!Number.isInteger(billingDay) || billingDay < 1 || billingDay > 31 || !mandate) return null;
  const today = startOfDay(now);
  const earliest = new Date(today.getFullYear(), today.getMonth(), today.getDate() + (mandate === 'missing' ? MANDATE_SETUP_DAYS : 1));
  const locked = new Set(lockedDates ?? []);

  for (let offset = 0; offset < 24; offset++) {
    const date = billingDayIn(earliest.getFullYear(), earliest.getMonth() + offset, billingDay);
    if (date >= earliest && !locked.has(isoDate(date))) return date;
  }
  return null;
}

/** The last collection of a limited order - the billing day of the month of its last payment; null without a limit. */
export function lastCollectionDate(first: Date, payments: number | null, billingDay: number): Date | null {
  if (payments == null) return null;
  return billingDayIn(first.getFullYear(), first.getMonth() + payments - 1, billingDay);
}

// ---------------------------------------------------------------- reading the fields

const trim = (s: string): string => s.trim();

/** The amount as a number of shekels and agorot, or null when it is not a positive amount. */
export function parseAmount(text: string): number | null {
  const value = Number(trim(text));
  if (trim(text) === '' || !Number.isFinite(value) || value <= 0) return null;
  return Math.round(value * 100) / 100;
}

/** The payments: null for "until the donor stops", a whole number 1-998, or undefined when not given or invalid. */
export function parsePayments(form: Pick<BankOrderForm, 'payments' | 'unlimited'>): number | null | undefined {
  if (form.unlimited) return null;
  const value = Number(trim(form.payments));
  return trim(form.payments) !== '' && Number.isInteger(value) && value >= 1 && value <= MAX_LIMITED_PAYMENTS ? value : undefined;
}

// ---------------------------------------------------------------- the steps

export function validateMandateAnswer(form: BankOrderForm): BankOrderErrors {
  return form.mandate ? {} : { mandate: 'required' };
}

/** How this form will reach the bank, or null while the coordinator has not said (a fax bank). */
export function formDeliveryOf(form: Pick<BankOrderForm, 'bank' | 'faxBy'>): FormDelivery | null {
  if (form.bank === BANK_BY_EMAIL) return 'bankEmail';
  return form.faxBy || null;
}

/** The signed form, and - for a bank that takes faxes only - who sends it. */
export function validateMandateForm(file: Pick<File, 'size' | 'type'> | null, form?: Pick<BankOrderForm, 'bank' | 'faxBy'>): BankOrderErrors {
  const errors: BankOrderErrors = {};
  if (!file) errors.mandateForm = 'mandateFormRequired';
  else if (file.type !== 'application/pdf' && !file.type.startsWith('image/')) errors.mandateForm = 'mandateFormType';
  else if (file.size > MANDATE_FORM_MAX_BYTES) errors.mandateForm = 'mandateFormTooLarge';
  if (form && formDeliveryOf(form) == null) errors.faxBy = 'faxByRequired';
  return errors;
}

/** The details, the same in both cases: everything is required except the e-mail address. */
export function validateDetails(form: BankOrderForm): BankOrderErrors {
  const errors: BankOrderErrors = {};
  if (!trim(form.donorName)) errors.donorName = 'required';
  if (!form.israeliId) errors.israeliId = 'required';
  else if (!isValidIsraeliID(form.israeliId)) errors.israeliId = 'israeliIdInvalid';
  if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(trim(form.email))) errors.email = 'emailInvalid';

  if (!form.bank) errors.bank = 'required';
  if (!form.branch) errors.branch = 'required';
  if (!form.account) errors.account = 'required';
  // What Masav carries: up to nine digits.
  else if (!/^\d{2,9}$/.test(form.account)) errors.account = 'accountInvalid';

  if (parseAmount(form.amount) == null) errors.amount = 'amountRequired';
  if (!form.unlimited) {
    if (trim(form.payments) === '') errors.payments = 'paymentsRequired';
    else if (parsePayments(form) === undefined) errors.payments = 'paymentsInvalid';
  }
  if (!form.billingDay) errors.billingDay = 'required';
  return errors;
}

/** The request for a form that passed every step. */
export function buildCreateRequest(
  form: BankOrderForm,
  requestId: string,
  firstCollection: Date,
  mandateForm: { fileName: string; contentType: string; base64: string } | null,
): CreateBankStandingOrderRequest {
  const amount = parseAmount(form.amount);
  const payments = parsePayments(form);
  const delivery = formDeliveryOf(form);
  if (!form.mandate || amount == null || payments === undefined || (form.mandate === 'missing' && (!mandateForm || !delivery))) {
    throw new Error('buildCreateRequest needs a form that passed validation');
  }

  return {
    requestId,
    mandateExists: form.mandate === 'exists',
    donorName: trim(form.donorName),
    israeliId: form.israeliId,
    email: trim(form.email) === '' ? undefined : trim(form.email),
    bank: pad(Number(form.bank), 2),
    branch: pad(Number(form.branch), 3),
    account: form.account,
    monthlyAmount: amount,
    payments,
    billingDay: Number(form.billingDay),
    firstCollection: isoDate(firstCollection),
    ...(form.mandate === 'missing' && mandateForm && delivery ? {
      formDelivery: delivery,
      mandateFormFileName: mandateForm.fileName,
      mandateFormContentType: mandateForm.contentType,
      mandateFormBase64: mandateForm.base64,
    } : {}),
  };
}

/** Whether anything has been typed or chosen - so a click outside asks before throwing it away. */
export const hasInput = (form: BankOrderForm, hasFile: boolean): boolean =>
  hasFile || form.unlimited || Object.values(form).some((value) => typeof value === 'string' && trim(value) !== '');
